import { useParams } from '@reach/router';
import Tippy from '@tippyjs/react';
import { graphql } from 'gatsby';
import React, { FC, useState } from 'react';

import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  Copy,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import { NextIcon, PreviousIcon, RefreshIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import { RgCraV2EventType, RgCraV2RiskHistoryOrderBy } from '@/globalTypes';
import { assert } from '@/utils/error';
import { findUnionMember } from '@/utils/find-union-member';
import { splitStringIntoTwoWords } from '@/utils/split-string-in-two-words';
import usePlayerRgRiskAssessmentHistoryLegacy from './usePlayerRgRiskAssessmentHistoryLegacy';

export const Fragment = graphql`
  fragment SanityPlayerRgRiskAssessmentHistoryLegacyBlock on SanityPlayerRgRiskAssessmentHistoryLegacyBlock {
    title {
      ...SanityLocaleString
    }
  }
`;

const PlayerRgRiskAssessmentHistoryLegacyBlock: FC<{
  block: Queries.SanityPlayerRgRiskAssessmentHistoryLegacyBlockFragment;
}> = ({ block }) => {
  const params = useParams();
  const { t } = useTranslate();

  assert(params.playerGlobalId, 'missing playerGlobalId');

  // Legacy data is rarely needed, so the card starts collapsed and the query
  // stays paused until someone expands it.
  const [collapsed, setCollapsed] = useState(true);

  const {
    history,
    fetching,
    nextPage,
    previousPage,
    refresh,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
  } = usePlayerRgRiskAssessmentHistoryLegacy(params.playerGlobalId, {
    paused: collapsed,
  });

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'playerRgRiskAssessmentHistoryLegacy',
    data: history,
    fetching,
    orderBy,
    setOrderBy,
    desc,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.dateTimeValue({
        field: 'processedAt',
        title: 'Time',
        fromFilterField: 'legacyFrom',
        toFilterField: 'legacyTo',
        orderBy: RgCraV2RiskHistoryOrderBy.processedAt,
      }),
      s.initiatorValue({
        field: 'initiator',
        title: 'Initiator',
      }),
      s.enumValue({
        field: 'eventType',
        title: 'Event Type',
        filterField: 'legacyEventType',
        e: RgCraV2EventType,
        getValue: ({ row }) => {
          const outcome = findUnionMember(row.outcomes, 'explanation');
          const eventTypeLabel = splitStringIntoTwoWords(row.eventType);

          return (
            <Tippy content={outcome?.explanation || eventTypeLabel}>
              <span>{eventTypeLabel}</span>
            </Tippy>
          );
        },
      }),
      s.stringValue({
        field: 'playerId',
        title: 'Player UUID',
        getValue: ({ row }) =>
          row.playerId ? (
            <Copy fetching={fetching} value={row.playerId} />
          ) : (
            '-'
          ),
      }),
      s.stringValue({
        field: 'comment',
        title: 'Comment',
        getValue: ({ row }) => row.comment,
      }),
      s.stringValue({
        field: 'eventId',
        title: 'Event ID',
        hidden: true,
        getValue: ({ row }) => row.eventId,
      }),
    ],
  });

  return (
    <Card
      size="lg"
      title={<div className="flex items-center gap-3">{t(block.title)}</div>}
      showOptionsAtBottom
      collapsible
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default PlayerRgRiskAssessmentHistoryLegacyBlock;
